import React, {
	forwardRef,
	memo,
	MutableRefObject,
	useEffect,
	useImperativeHandle,
	useRef,
} from "react";
import { useStableCallback } from "../../hooks/useStableCallback";

import { createSlideShow, ISlideShow } from "../slideshow/slideshow-js";

import "../slideshow/slideshow.css";
import "./slideshow-preview.css";
import { ISpreadSlideShowItem } from "./spread-slideshow-item";

/** Use this type for typing the ref result */
export interface ISlidesShowPreviewRef {
	el: HTMLDivElement;
	previewEl: HTMLDivElement;
	slideShow: ISlideShow<ISpreadSlideShowItem>;
}

/**
 * Add double click handler on document
 * Since React v17, our double click on the element did not work on iOS
 * - TODO: Investigate why onDblClick on element did not work (e.stopPropagation)
 */
function useDoubleClick(
	ref: MutableRefObject<HTMLElement | null>,
	onDoubleClick: (e: MouseEvent) => void,
) {
	const stableOnDoubleClick = useStableCallback((e: MouseEvent) => {
		if (!ref.current) return;

		// Clicked on the target?
		if (e.composedPath?.().includes(ref.current)) {
			onDoubleClick(e);
		}
	});

	useEffect(() => {
		document.addEventListener("dblclick", stableOnDoubleClick);

		return () => {
			document.removeEventListener("dblclick", stableOnDoubleClick);
		};
	}, [stableOnDoubleClick]);
}

export const SlideshowPreview = memo(
	forwardRef<
		ISlidesShowPreviewRef,
		{
			// Better to move onClick outside to useGesture?
			onClick?: React.DOMAttributes<HTMLDivElement>["onClick"];
			onMouseMove?: React.DOMAttributes<HTMLDivElement>["onMouseMove"];
			onDoubleClick: (e: MouseEvent) => void;
		}
	>(({ onClick, onDoubleClick, onMouseMove }, ref) => {
		const slideShowElRef = useRef<HTMLDivElement>(null);
		const slideShowPreviewRef = useRef<HTMLDivElement>(null);

		useDoubleClick(slideShowPreviewRef, onDoubleClick);

		/** Expose SlideShow object via ref */
		useImperativeHandle(
			ref,
			() => {
				if (!slideShowElRef.current || !slideShowPreviewRef.current) {
					throw new Error("useImperativeHandle: No SlideShowPreview element");
				}

				const slideShow = createSlideShow(
					slideShowElRef.current,
				) as ISlideShow<ISpreadSlideShowItem>;

				// Make slideshow available on console in 'Debug' mode
				// Assuming only one slideshow item is used, else global variable overridden
				if (eXpress.isDebugMode) {
					(window as any)._slideShow = slideShow;
				}

				return {
					el: slideShowElRef.current,
					previewEl: slideShowPreviewRef.current,
					slideShow,
				};
			},
			[],
		);

		return (
			// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
			<div
				className="slideshow-preview"
				onClick={onClick || (() => undefined)}
				role="img"
				ref={slideShowPreviewRef}
				onMouseMove={onMouseMove}
			>
				{/* Parent fullscreen div needed for drag handles */}
				<div ref={slideShowElRef} />
			</div>
		);
	}),
);
SlideshowPreview.displayName = "SlideshowPreview";
